import Component from './Component';
import SectionComponent from './SectionComponent';
import BlockComponent from './BlockComponent';

/**
 * @class ComponentManager
 * @classdesc Class representing a component manager.
 */
export default class ComponentManager {
  /**
   * @constructor
   */
  constructor() {
    this._componentClasses = {};
    this._instances = {};
    this._deferredEvents = [];
    this._onShopifyEvent = this._onShopifyEvent.bind(this);
    this._awaitingComponents = [];

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        // create a new instance of `MutationObserver` named `observer`,
        // passing it a callback function
        const observer = new MutationObserver(mutationsList => {
          for (let i = 0; i < mutationsList.length; i++) {
            const mutation = mutationsList[i];
            const { type, addedNodes, removedNodes } = mutation;

            if (type === 'childList') {
              removedNodes.forEach(removedNode => {
                // Only want to look at element nodes
                if (removedNode.nodeType === Node.ELEMENT_NODE) {
                  const componentEls = removedNode.dataset && 'component' in removedNode.dataset
                    ? [removedNode, ...removedNode.querySelectorAll('[data-component]')]
                    : removedNode.querySelectorAll('[data-component]');

                  componentEls.forEach(componentEl => this._destroyInstance(componentEl));
                }
              });

              addedNodes.forEach(addedNode => {
                // Only want to look at element nodes
                if (addedNode.nodeType === Node.ELEMENT_NODE) {
                  const componentEls = addedNode.dataset && 'component' in addedNode.dataset
                    ? [addedNode, ...addedNode.querySelectorAll('[data-component]')]
                    : [...addedNode.querySelectorAll('[data-component]')];

                  const components = componentEls
                    .map(componentEl => this._createInstance(componentEl))
                    .filter(componentEl => !!componentEl);

                  components.forEach(component => this._mountComponent(component));

                  this._awaitingComponents.forEach(awaitingComponents => {
                    if (awaitingComponents.el === addedNode || addedNode.contains(awaitingComponents.el)) {
                      awaitingComponents.callback(components);
                    }
                  });
                }
              });

              if (this._deferredEvents.length) {
                this._deferredEvents.forEach(deferredEvent => this._onShopifyEvent(deferredEvent, true));
                this._deferredEvents = [];
              }
            }
          }
        });

        // call `observe()` on that MutationObserver instance,
        // passing it the element to observe, and the options object
        observer.observe(document.body, { subtree: true, childList: true });
        document.addEventListener('shopify:section:select', this._onShopifyEvent);
        document.addEventListener('shopify:section:deselect', this._onShopifyEvent);
        document.addEventListener('shopify:section:reorder', this._onShopifyEvent);
        document.addEventListener('shopify:block:select', this._onShopifyEvent);
        document.addEventListener('shopify:block:deselect', this._onShopifyEvent);

        this._initializeComponents();
      },
      {
        once: true,
      },
    );
  }

  /**
   * Get all component instances
   *
   * @memberof ComponentManager.prototype
   * @type {Object.<string, Component>} A map of component names to components.
   */
  get instances() {
    return this._instances;
  }

  /**
   * Get component hierarchy
   *
   * @memberof ComponentManager.prototype
   * @type {Object.<string, Object>} A hierarchical map of components in the DOM
   */
  get hierarchy() {
    const outputFn = components => components.reduce((acc, component) => {
      acc[`${component.name}${component.tag ? `:${component.tag}` : ''}:${component.id}`] = outputFn(component.children);

      return acc;
    }, {});

    const rootComponents = Object.keys(this._instances)
      .reduce((acc, id) => {
        const component = this._instances[id];

        if (!component.parent) {
          acc.push(component);
        }

        return acc;
      }, []);

    return outputFn(rootComponents);
  }

  /**
   * Register a component
   *
   * @memberof ComponentManager.prototype
   * @param {Component} componentClass A component class
   * @param {string} name The component name in kebab case
   */
  register(componentClass, name) {
    // Pascal to kebab case
    const kebabCaseName = name
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .toLowerCase();

    if (!(kebabCaseName in this._componentClasses)) {
      this._componentClasses[kebabCaseName] = {};
    }

    // Store the section handler
    this._componentClasses[kebabCaseName] = componentClass;

    // Initialize just these components if document is already loaded
    if (document.readyState === 'loaded') {
      this._initializeComponents(kebabCaseName);
    }
  }

  /**
   * Get a component by id or HTMLElement
   *
   * @memberof ComponentManager.prototype
   * @param {HTMLElement|string} selector A component element or ID
   * @returns {Component} The modified param.
   */
  getComponent(selector) {
    if (selector instanceof HTMLElement) {
      return this._instances[selector.id];
    }

    return this._instances[selector];
  }

  /**
   * Get components of a newly added DOM elements
   *
   * @memberof ComponentManager.prototype
   * @param {HTMLElement|string} element An element that contains one or more components
   * @param {Number} [timeout] The maximum time to wait for components to mount
   * @returns {Component[]} A list of mounted components
   */
  awaitComponents(el, timeout = 5000) {
    const components = [];
    let componentEls = [];

    if (el.hasAttribute('data-component')) {
      componentEls.push(el);
    }

    el
      .querySelectorAll('[data-component]')
      .forEach(componentEl => componentEls.push(componentEl));

    // Add any existing components to the component list
    // and reduce the amount of elements we're waiting
    // to be mounted
    componentEls = componentEls.reduce((acc, componentEl) => {
      const awaitedComponent = this.getComponent(componentEl);

      if (awaitedComponent) {
        components.push(awaitedComponent);

        return acc;
      }

      acc.push(componentEl);

      return acc;
    }, []);

    if (!componentEls.length) {
      return Promise.resolve(components);
    }

    return new Promise(resolve => {
      let timer = null;
      const entry = {};

      entry.el = el;
      entry.callback = (newComponents = null) => {
        // Add any awaited components to the component list
        // and reduce the amount of elements we're waiting
        // to be mounted
        componentEls = componentEls.reduce((acc, componentEl) => {
          const awaitedComponent = newComponents.find(newComponent => newComponent.el === componentEl);

          if (awaitedComponent) {
            components.push(awaitedComponent);

            return acc;
          }

          acc.push(componentEl);

          return acc;
        }, []);

        // No more components to await on, so we'll resolve
        if (!componentEls.length) {
          clearTimeout(timer);
          this._awaitingComponents.splice(this._awaitingComponents.indexOf(entry), 1);
          resolve(components);
        }
      }

      timer = setTimeout(
        () => {
          console.debug(`ComponentManager: awaitComponents has reached timeout after ${timeout}ms.`);
          console.debug(el);
          resolve(components);
        },
        timeout,
      );

      this._awaitingComponents.push(entry);
    });
  }

  /**
   * Get first child component with given parameters
   *
   * @memberof ComponentManager.prototype
   * @param {string} name - A component name
   * @param {string} [tag] - A component tag
   * @return {Component|null} The first child component
   *                          with a given name and tag, null if not found.
   */
  query(name, tag, scope = document.body) {
    const componentSelector = name ? `[data-component="${name}"]` : '[data-component]';
    const tagSelector = tag ? `[data-component-tag="${tag}]` : '';
    const result = scope.querySelector(`${componentSelector}${tagSelector}`);

    if (!result) return null;

    return this.getComponent(result.getAttribute('id'));
  }

  /**
   * Get all children components with given parameters
   *
   * @memberof ComponentManager.prototype
   * @param {string} name - A component name
   * @param {string} [tag] - A component tag
   * @return {Component[]} An array of child components
   *                          with a given name and tag, an empty array if none are found.
   */
  queryAll(name = '', tag = '', scope = document.body) {
    const componentSelector = name ? `[data-component="${name}"]` : '[data-component]';
    const tagSelector = tag ? `[data-component-tag="${tag}]` : '';
    const results = [...scope.querySelectorAll(`${componentSelector}${tagSelector}`)];

    if (!results.length) return [];

    return results.reduce((acc, result) => {
      const component = this.getComponent(result.getAttribute('id'));

      if (component) {
        acc.push(component);
      }

      return acc;
    }, []);
  }

  /**
   * Initialize components already on the page.
   *
   * @private
   */
  _initializeComponents(name = null) {
    // Fetch all existing sections of our type on the page
    const componentEls = name
      ? [...document.querySelectorAll(`[data-component="${name}"]`)]
      : [...document.querySelectorAll('[data-component]')];

    if (!componentEls.length) return;

    // Create an instance for each section
    const components = componentEls
      .map(componentEl => this._createInstance(componentEl))
      .filter(componentEl => !!componentEl);

    components.forEach(component => this._mountComponent(component));
  }

  /**
   * Mount a component and emit an event
   *
   * @private
   */
  _mountComponent(component) {
    component.onMount();

    const event = new CustomEvent('core:component-mount', { detail: { component }, bubbles: true });

    component.el.dispatchEvent(event);
  }

  /**
   * Dismount a component and emit an event
   *
   * @private
   */
  _dismountComponent(component) {
    component.onDismount();

    const event = new CustomEvent('core:component-dismount', { detail: { component }, bubbles: true });

    component.el.dispatchEvent(event);
  }

  /**
   * Trigger associated events on components based on Shopify lifecycle.
   *
   * @private
   */
  _onShopifyEvent(event, deferred = false) {
    const { target, type } = event;
    const { sectionId, blockId, load } = event.detail;
    let component = null;
    let parentComponent = null;

    if (deferred) {
      console.debug(`ComponentManager: Deferred event '${type}' for${blockId ? ` Block ${blockId} within` : ''} Section ${sectionId} received.`);
    } else {
      console.debug(`ComponentManager: Event '${type}' for${blockId ? ` Block ${blockId} within` : ''} Section ${sectionId} received.`);
    }

    // A Shopify event with the flag 'load' means that the DOM was changed. We need to defer this event and handle it when
    // the component is mounted via the MutationObserver.
    if (!deferred && load) {
      this._deferredEvents.push(event);
      console.debug(`ComponentManager: Event '${type}' for${blockId ? ` Block ${blockId} within` : ''} Section ${sectionId} deferred.`);
    }

    if (blockId) {
      component = this.getComponent(target); // Block is target

      // Inform the parent section that one of its blocks has been selected/deselected
      if (sectionId) {
        const parentElement = document.querySelector(`#shopify-section-${sectionId}`);
        if (parentElement) {
          parentComponent = this.getComponent(parentElement.firstElementChild);
        }
      }

      if (!component && !parentComponent) return; // No block or section to handle event
    } else if (sectionId) {
      component = this.getComponent(target.firstElementChild); // Section is wrapped by target

      if (!component || component.type !== 'section') return; // Component type mismatch
    }

    switch (event.type) {
      case 'shopify:section:load':
        // Uneeded, observer initializes this
        break;
      case 'shopify:section:unload':
        this._dismountComponent(component);
        delete this._instances[component.id];
        break;
      case 'shopify:section:select':
        component.onSelect(event);
        break;
      case 'shopify:section:deselect':
        component.onDeselect(event);
        break;
      case 'shopify:section:reorder':
        component.onReorder(event);
        break;
      case 'shopify:block:select':
        if (component) {
          component.onSelect(event);
        }

        if (parentComponent) {
          parentComponent.onBlockSelect(event);
        }
        break;
      case 'shopify:block:deselect':
        if (component) {
          component.onDeselect(event);
        }

        if (parentComponent) {
          parentComponent.onBlockDeselect(event);
        }
        break;
      default:
        break;
    }
  }

  _destroyInstance(componentEl) {
    const id = componentEl.getAttribute('id');
    const { component: name } = componentEl.dataset;
    const component = this._instances[id];

    if (component) {
      this._dismountComponent(component);
      delete this._instances[id];
      console.debug(`ComponentManager: Component '${name}' successfully destroyed.`);
    } else {
      console.debug(`ComponentManager: Component '${name}' failed to destroy.`);
    }
  }

  /**
   * Creates a new instances of a component.
   *
   * @param {HTMLElement} componentEl - The component root element
   * @private
   */
  _createInstance(componentEl) {
    const { component: name } = componentEl.dataset;

    if (!name) return null;

    let ComponentClass = null;

    if (name in this._componentClasses) {
      ComponentClass = this._componentClasses[name];
    } else {
      switch (componentEl.dataset.componentType) {
        case 'section':
          ComponentClass = SectionComponent;
          break;
        case 'block':
          ComponentClass = BlockComponent;
          break;
        case 'component':
        default:
          ComponentClass = Component;
          break;
      }
    }

    const parentComponentEl = componentEl.parentElement ? componentEl.parentElement.closest('[data-component]') : null;
    const parentComponent = parentComponentEl ? this.getComponent(parentComponentEl.id) : null;

    const context = {
      ...PXUTheme.context,
      ...(
        parentComponent
          ? {
            ...parentComponent.context,
            ...parentComponent.state,
          }
          : {}
      ),
    };

    try {
      const component = new ComponentClass(componentEl, context);

      this._instances[component.id] = component;

      console.debug(`ComponentManager: Component '${name}' successfully initialized.`);

      return component;
    } catch (e) {
      console.group(`ComponentManager: Component '${name}' failed to initialize.`);
      console.error(e);
      if ('cause' in e) {
        console.error(e.cause);
      }

      console.groupEnd();

      return null;
    }
  }
}
